/**
 * Binary wire protocol.
 *
 * Everything is little-endian. Variable-length integers are used for
 * quantities whose magnitude varies a lot (block state ids, entity ids, chunk
 * palettes) and fixed widths for everything else. Chunk sections use a palette
 * plus bit-packed indices, which combined with the WebSocket's permessage-
 * deflate keeps a full chunk column in the low single-digit kilobytes.
 */

export const PROTOCOL_VERSION = 7;

// --- server -> client -------------------------------------------------------
export const S2C = {
  HELLO: 0,
  CHUNK: 1,
  CHUNK_UNLOAD: 2,
  BLOCK_CHANGE: 3,
  MULTI_BLOCK_CHANGE: 4,
  LIGHT: 5,
  TIME: 6,
  PLAYER_STATE: 7,
  POSITION_CORRECTION: 8,
  INVENTORY: 9,
  SLOT: 10,
  ENTITY_SPAWN: 11,
  ENTITY_UPDATE: 12,
  ENTITY_REMOVE: 13,
  CHAT: 14,
  OPEN_CONTAINER: 15,
  CONTAINER_UPDATE: 16,
  CLOSE_CONTAINER: 17,
  SOUND: 18,
  PARTICLE: 19,
  DIMENSION: 20,
  BLOCK_ENTITY: 21,
  STATS: 22,
  BLOCK_BREAK_PROGRESS: 23,
  BOSS_BAR: 24,
  RESPAWN_INFO: 25,
} as const;

// --- client -> server -------------------------------------------------------
export const C2S = {
  JOIN: 0,
  MOVE: 1,
  BREAK_START: 2,
  BREAK_CANCEL: 3,
  BREAK_FINISH: 4,
  PLACE: 5,
  SELECT_SLOT: 6,
  INVENTORY_CLICK: 7,
  DROP: 8,
  CHAT: 9,
  RESPAWN: 10,
  ATTACK: 11,
  INTERACT: 12,
  CLOSE_CONTAINER: 13,
  USE_ITEM: 14,
  CRAFT: 15,
  SET_RENDER_DISTANCE: 16,
  SWING: 17,
  PING: 18,
  TOGGLE_FLY: 19,
} as const;

export class ByteWriter {
  private buf: Uint8Array;
  private view: DataView;
  private pos = 0;

  constructor(capacity = 1024) {
    this.buf = new Uint8Array(capacity);
    this.view = new DataView(this.buf.buffer);
  }

  private ensure(n: number): void {
    if (this.pos + n <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < this.pos + n) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.buf.subarray(0, this.pos));
    this.buf = next;
    this.view = new DataView(next.buffer);
  }

  u8(v: number): this {
    this.ensure(1);
    this.buf[this.pos++] = v & 0xff;
    return this;
  }

  i8(v: number): this {
    this.ensure(1);
    this.view.setInt8(this.pos++, v);
    return this;
  }

  u16(v: number): this {
    this.ensure(2);
    this.view.setUint16(this.pos, v, true);
    this.pos += 2;
    return this;
  }

  i16(v: number): this {
    this.ensure(2);
    this.view.setInt16(this.pos, v, true);
    this.pos += 2;
    return this;
  }

  u32(v: number): this {
    this.ensure(4);
    this.view.setUint32(this.pos, v >>> 0, true);
    this.pos += 4;
    return this;
  }

  i32(v: number): this {
    this.ensure(4);
    this.view.setInt32(this.pos, v | 0, true);
    this.pos += 4;
    return this;
  }

  f32(v: number): this {
    this.ensure(4);
    this.view.setFloat32(this.pos, v, true);
    this.pos += 4;
    return this;
  }

  f64(v: number): this {
    this.ensure(8);
    this.view.setFloat64(this.pos, v, true);
    this.pos += 8;
    return this;
  }

  /** LEB128 unsigned varint. */
  varint(v: number): this {
    let x = v >>> 0;
    do {
      let byte = x & 0x7f;
      x >>>= 7;
      if (x !== 0) byte |= 0x80;
      this.u8(byte);
    } while (x !== 0);
    return this;
  }

  /** Zig-zag encoded signed varint. */
  svarint(v: number): this {
    return this.varint(((v << 1) ^ (v >> 31)) >>> 0);
  }

  bool(v: boolean): this {
    return this.u8(v ? 1 : 0);
  }

  str(s: string): this {
    const bytes = TEXT_ENCODER.encode(s);
    this.varint(bytes.length);
    this.bytes(bytes);
    return this;
  }

  bytes(b: Uint8Array): this {
    this.ensure(b.length);
    this.buf.set(b, this.pos);
    this.pos += b.length;
    return this;
  }

  /** Write a length-prefixed byte blob. */
  blob(b: Uint8Array): this {
    this.varint(b.length);
    return this.bytes(b);
  }

  get length(): number {
    return this.pos;
  }

  finish(): Uint8Array {
    return this.buf.subarray(0, this.pos);
  }

  /** Copy out so the result survives further writes to this writer. */
  toBuffer(): Uint8Array {
    return this.buf.slice(0, this.pos);
  }
}

export class ByteReader {
  private view: DataView;
  private pos = 0;
  readonly buf: Uint8Array;

  constructor(data: Uint8Array | ArrayBuffer) {
    this.buf = data instanceof Uint8Array ? data : new Uint8Array(data);
    this.view = new DataView(this.buf.buffer, this.buf.byteOffset, this.buf.byteLength);
  }

  get remaining(): number {
    return this.buf.length - this.pos;
  }

  u8(): number {
    return this.buf[this.pos++]!;
  }

  i8(): number {
    return this.view.getInt8(this.pos++);
  }

  u16(): number {
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }

  i16(): number {
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }

  u32(): number {
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }

  i32(): number {
    const v = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return v;
  }

  f32(): number {
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }

  f64(): number {
    const v = this.view.getFloat64(this.pos, true);
    this.pos += 8;
    return v;
  }

  varint(): number {
    let result = 0;
    let shift = 0;
    for (;;) {
      const byte = this.buf[this.pos++]!;
      result |= (byte & 0x7f) << shift;
      if ((byte & 0x80) === 0) break;
      shift += 7;
      if (shift > 35) throw new Error('varint too long');
    }
    return result >>> 0;
  }

  svarint(): number {
    const v = this.varint();
    return (v >>> 1) ^ -(v & 1);
  }

  bool(): boolean {
    return this.u8() !== 0;
  }

  str(): string {
    const len = this.varint();
    const s = TEXT_DECODER.decode(this.buf.subarray(this.pos, this.pos + len));
    this.pos += len;
    return s;
  }

  bytes(len: number): Uint8Array {
    const b = this.buf.subarray(this.pos, this.pos + len);
    this.pos += len;
    return b;
  }

  blob(): Uint8Array {
    return this.bytes(this.varint());
  }
}

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();

// ---------------------------------------------------------------------------
// Section encoding
// ---------------------------------------------------------------------------

export const SECTION_FORMAT = {
  /** Every block in the section is the same state. */
  SINGLE: 0,
  /** Palette of <=255 entries, one byte per block. */
  BYTE_PALETTE: 1,
  /** Raw 16-bit state ids. */
  DIRECT: 2,
} as const;

/** Encode 4096 block states with a palette when that is smaller than raw. */
export function writeSection(w: ByteWriter, blocks: Uint16Array): void {
  const palette: number[] = [];
  const lookup = new Map<number, number>();
  let distinct = 0;
  for (let i = 0; i < blocks.length; i++) {
    const s = blocks[i]!;
    if (!lookup.has(s)) {
      lookup.set(s, distinct++);
      palette.push(s);
      if (distinct > 255) break;
    }
  }

  if (distinct === 1) {
    w.u8(SECTION_FORMAT.SINGLE);
    w.varint(palette[0]!);
    return;
  }
  if (distinct <= 255) {
    w.u8(SECTION_FORMAT.BYTE_PALETTE);
    w.varint(palette.length);
    for (const s of palette) w.varint(s);
    const indices = new Uint8Array(blocks.length);
    for (let i = 0; i < blocks.length; i++) indices[i] = lookup.get(blocks[i]!)!;
    w.bytes(indices);
    return;
  }
  w.u8(SECTION_FORMAT.DIRECT);
  w.bytes(new Uint8Array(blocks.buffer, blocks.byteOffset, blocks.byteLength));
}

export function readSection(r: ByteReader, out: Uint16Array): void {
  const format = r.u8();
  if (format === SECTION_FORMAT.SINGLE) {
    out.fill(r.varint());
    return;
  }
  if (format === SECTION_FORMAT.BYTE_PALETTE) {
    const n = r.varint();
    const palette = new Uint16Array(n);
    for (let i = 0; i < n; i++) palette[i] = r.varint();
    const indices = r.bytes(out.length);
    for (let i = 0; i < out.length; i++) out[i] = palette[indices[i]!]!;
    return;
  }
  const raw = r.bytes(out.length * 2);
  // The source may not be 2-byte aligned inside the socket frame.
  for (let i = 0; i < out.length; i++) out[i] = raw[i * 2]! | (raw[i * 2 + 1]! << 8);
}

// ---------------------------------------------------------------------------
// Shared payload shapes
// ---------------------------------------------------------------------------

export interface NetItemStack {
  item: number;
  count: number;
  damage: number;
}

export function writeStack(w: ByteWriter, stack: NetItemStack | null): void {
  if (!stack || stack.count <= 0) {
    w.u8(0);
    return;
  }
  w.u8(1);
  w.varint(stack.item);
  w.u8(stack.count);
  w.varint(stack.damage);
}

export function readStack(r: ByteReader): NetItemStack | null {
  if (r.u8() === 0) return null;
  const item = r.varint();
  const count = r.u8();
  const damage = r.varint();
  return { item, count, damage };
}

/** Pack a block position relative to a chunk into 16 bits (x,z 4 bits, y 9 bits with sign). */
export function packLocalPos(lx: number, y: number, lz: number): number {
  return ((y + 64) << 8) | (lz << 4) | lx;
}

export function unpackLocalPos(v: number): [number, number, number] {
  return [v & 0xf, (v >> 8) - 64, (v >> 4) & 0xf];
}
